import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Loader2 } from "lucide-react";

export default function Embed() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = searchParams.get("token");
    const locationId = searchParams.get("location_id");
    const masterKey = searchParams.get("key");
    const userData = searchParams.get("userData");

    const finishLogin = (data) => {
      if (data && data.access_token) {
        loginWithToken(data.access_token, data.user);
        navigate("/", { replace: true });
      } else {
        setError("Invalid response from server");
        setLoading(false);
      }
    };

    const fail = (err, fallback) => {
      console.error("Embed login error:", err);
      setError(err?.response?.data?.detail || fallback);
      setLoading(false);
    };

    // Flow A: classic per-business embed token (or agency master key + location)
    const tokenLogin = async () => {
      try {
        const params = new URLSearchParams();
        if (token) params.set("token", token);
        if (masterKey) params.set("key", masterKey);
        if (locationId) params.set("location_id", locationId);
        const response = await api.get(`/auth/embed?${params.toString()}`);
        finishLogin(response.data);
      } catch (err) {
        fail(err, "Failed to authenticate");
      }
    };

    // Flow B: GHL SSO (Custom Page iframe) - exchange encrypted userData for a session
    const ssoLogin = async (payload) => {
      try {
        const response = await api.post("/auth/sso", { payload });
        finishLogin(response.data);
      } catch (err) {
        fail(err, "SSO authentication failed");
      }
    };

    if (token || (masterKey && locationId)) {
      tokenLogin();
      return;
    }

    if (userData) {
      ssoLogin(userData);
      return;
    }

    // No params: we must be inside the GHL iframe - ask the parent for the user context
    if (window.parent === window) {
      setError("Abre LeadBot desde el menú lateral de tu panel.");
      setLoading(false);
      return;
    }

    const onMessage = (event) => {
      const d = event.data;
      if (!d || typeof d !== "object") return;
      // Official GHL response name is REQUEST_USER_DATA_RESPONSE;
      // keep RESPONSE_USER_DATA as fallback for older implementations.
      const isSsoResponse =
        d.message === "REQUEST_USER_DATA_RESPONSE" ||
        d.message === "RESPONSE_USER_DATA";
      const payload = d.payload || d.data;
      if (isSsoResponse && payload && typeof payload === "string") {
        window.removeEventListener("message", onMessage);
        clearTimeout(timeout);
        ssoLogin(payload);
      }
    };
    window.addEventListener("message", onMessage);
    window.parent.postMessage({ message: "REQUEST_USER_DATA" }, "*");

    const timeout = setTimeout(() => {
      window.removeEventListener("message", onMessage);
      setError("No se recibió la sesión. Recarga la página.");
      setLoading(false);
    }, 10000);

    return () => {
      window.removeEventListener("message", onMessage);
      clearTimeout(timeout);
    };
  }, [searchParams, navigate, loginWithToken]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Conectando con tu cuenta...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h1 className="text-xl font-semibold text-foreground mb-2">Authentication Error</h1>
          <p className="text-muted-foreground mb-4">{error}</p>
          <p className="text-sm text-muted-foreground">
            Please contact your administrator or try again later.
          </p>
        </div>
      </div>
    );
  }

  return null;
}
